import type { OptionList, OptionListSummary } from '@src/models/interfaces/optionList.interface';
import { normalizeSearchText } from '@src/components/Form/DynamicForm/selectFilterOption';
import { OptionListMapper } from '@src/models/mappers/optionList.mapper';
import OptionListRepository from '@src/repositories/OptionListRepository';

class OptionListService {
  private repository: OptionListRepository;

  constructor(repository = new OptionListRepository()) {
    this.repository = repository;
  }

  /**
   * All lists of the organization, or, with `divisionIds` (a form's divisions), only the ones that
   * form may use: org-wide lists, plus the division's own lists when the form belongs to exactly
   * one division. A form shared by several divisions (or org-wide, `[]`) only uses org-wide lists,
   * so one division's editors never decide what another division's users are asked.
   */
  async list(organizationId: string, divisionIds?: string[]): Promise<OptionListSummary[]> {
    const lists = divisionIds
      ? await this.repository.listForDivisions(organizationId, divisionIds.length === 1 ? divisionIds : [])
      : await this.repository.listAll(organizationId);
    return lists
      .map(OptionListMapper.toSummary)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /** From the division mirror when `divisionId` is given (division users), else the org list. */
  async get(organizationId: string, listId: string, divisionId?: string): Promise<OptionList | null> {
    const stored = await this.repository.get(organizationId, listId, divisionId);
    return stored ? OptionListMapper.toOptionList(stored.list, stored.chunks) : null;
  }

  /** `divisionId` makes the list belong to that division; omit it for an org-wide list. */
  create(organizationId: string, name: string, labels: string[], divisionId?: string | null): Promise<string> {
    return this.repository.create({
      organizationId,
      name: name.trim(),
      labels,
      ...(divisionId ? { divisionId } : {}),
    });
  }

  /**
   * Replaces the active items with `labels`. Labels matching an existing item (ignoring case and
   * accents) keep its id so past answers stay linked; items no longer listed are deactivated.
   */
  update(organizationId: string, list: OptionList, name: string, labels: string[]): Promise<number> {
    const idsByLabel = new Map(list.items.map(item => [normalizeSearchText(item.label), item.id]));
    return this.repository.update({
      organizationId,
      ...(list.divisionId ? { divisionId: list.divisionId } : {}),
      listId: list.id,
      name: name.trim(),
      // New labels go without `id`: callables send `undefined` as null, which the API rejects.
      items: labels.map(label => {
        const id = idsByLabel.get(normalizeSearchText(label));
        return id ? { id, label } : { label };
      }),
    });
  }

  archive(organizationId: string, list: OptionListSummary, archived: boolean): Promise<number> {
    return this.repository.archive({
      organizationId,
      ...(list.divisionId ? { divisionId: list.divisionId } : {}),
      listId: list.id,
      archived,
    });
  }
}

export default OptionListService;
